import { ComponentChildren, JSX } from 'preact';

import { classNames } from '#utils/UNSAFE_classNames';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { Size } from '#utils/UNSAFE_size';
import { labelSlotStyles, labelInnerStyles } from './LabelValueLayoutStyles.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { TextFieldRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/TextFieldTheme';
import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/FormLayoutTheme';
import { StyledLabelValueLayout } from './StyledLabelValueLayout';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { computeStartLabelWidth } from '#utils/UNSAFE_formControlUtils';

type TextFieldProps = {
  /**
   * Defines how the label is going to be positioned. For a TextField only the start and top values are allowed.
   */
  labelEdge?: 'start' | 'top';
  /**
   * Specifies if the parent component is a TextField or TextArea
   */
  parentComponentVariant?: 'textField' | 'textArea';
};

type FormComponentWrapperProps = {
  /**
   * The start, top and inside values are supported for a wrapped component.
   */
  labelEdge?: 'start' | 'top' | 'inside';
  /**
   * Specifies if the parent component is a Form Component Wrapper
   */
  parentComponentVariant: 'formComponentWrapper';
  /**
   * Specifies if there is assistive text or links at the top of the component below the label
   */
  hasTopUserAssistance?: boolean;
};
// The 'unknown' variant is used by FormGenerator when labeling a component that it does not
// recognize as a known legacy component.  In such a case, we want to top align a start label
// instead of centering it, for example.
type UnknownParentProps = {
  /**
   * Defines how the label is going to be positioned. For unknown types of parent components only
   * the start and top values are allowed.
   */
  labelEdge?: 'start' | 'top';
  parentComponentVariant: 'unknown';
};

type Props = (TextFieldProps | FormComponentWrapperProps | UnknownParentProps) & {
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;
};

/**
 * A layout component for rendering a label and value pair, used in form components
 * such as TextField, TextArea, and FormComponentWrapper.
 */
export const LabelValueLayout = ({
  columnSpan = 1,
  label,
  labelEdge,
  children,
  labelStartWidth = '33%',
  parentComponentVariant = 'textField',
  ...rest
}: Props) => {
  const { baseTheme: textFieldTheme } = useComponentTheme(TextFieldRedwoodTheme);
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);
  const isTextField = parentComponentVariant.startsWith('text');
  const isFormComponentWrapper = parentComponentVariant === 'formComponentWrapper';
  const isUnknownParentComponentVariant = parentComponentVariant === 'unknown';
  const isStart = labelEdge === 'start';
  const isTop = labelEdge === 'top' || labelEdge === 'inside'; // 'inside' needs to pick up all 'top' styles.
  const isInside = labelEdge === 'inside';

  // JET-68190 - labelStartWidth needs to take columnSpan into account
  const width = computeStartLabelWidth(labelStartWidth, columnSpan);

  const valueWidth = `calc(100% - ${width})`;
  const hasTopUserAssistance = !!(rest as FormComponentWrapperProps).hasTopUserAssistance;

  const labelStyles: JSX.CSSProperties = isStart
    ? { flexBasis: width, width: width, maxWidth: width }
    : {};

  const valueStyles: JSX.CSSProperties = isStart
    ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth }
    : {};

  const { isFormLayout, isReadonly: isFormReadonly, labelWrapping } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);

  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    isStart && labelSlotStyles.start,
    isTop && labelSlotStyles.top,
    isTop && hasTopUserAssistance && labelSlotStyles.topAndTopUA,
    isTop && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);

  const labelInnerClasses = classNames([
    isTextField && textFieldTheme,
    // JET-70804 - preact start label not centered when used outside of a core pack custom element
    // always include the labelTheme so that CSS variables that LabelValueLayout depends on
    // are always defined
    labelTheme,
    formLayoutTheme,
    labelInnerStyles.base,
    isStart && labelInnerStyles.start,
    isUnknownParentComponentVariant && isStart && labelInnerStyles.startUnknownParent,
    isTextField && isStart && !isPureReadonly && labelInnerStyles.startNotPureReadonly,
    isFormComponentWrapper && isInside && labelInnerStyles.insideFormComponentWrapper,

    // Form Component Wrapper
    isFormComponentWrapper &&
      isStart &&
      !isPureReadonly &&
      !hasTopUserAssistance &&
      labelInnerStyles.startWithoutTopFormComponentWrapperUA,
    labelWrapping === 'truncate' && labelInnerStyles.noWrap
  ]);

  return (
    <StyledLabelValueLayout
      // For readonly start aligned labels, we want the div that is the parent of the label to
      // stretch to the height of the value content, including the end content (InputSensitiveText)
      flexAlign={isReadonly && isStart ? 'stretch' : 'start'}
      labelStyleClasses={labelStyleClasses}
      labelStyles={labelStyles}
      labelInnerClasses={labelInnerClasses}
      label={label}
      valueStyles={valueStyles}>
      {children}
    </StyledLabelValueLayout>
  );
};
